import {
  type Selection,
  type Store,
  addToSelection,
  createLens,
  loopsOnLayer,
  pruneHiddenFromSelection,
  selectionOf,
} from '@frontend/viewer-core';

import type { DrawingModel, DxfSessionState, LayerManager } from './types';
import type { ViewportController } from './viewportController';

export const createHiddenLayersSlice = (
  state: Store<DxfSessionState>,
): Store<ReadonlySet<number>> =>
  createLens(
    state,
    (current) => current.hiddenLayers,
    (current, hiddenLayers) => ({ ...current, hiddenLayers }),
  );

export const createHoveredLayerSlice = (
  state: Store<DxfSessionState>,
): Store<number | null> =>
  createLens(
    state,
    (current) => current.hoveredLayer,
    (current, hoveredLayer) => ({ ...current, hoveredLayer }),
  );

type Dependencies = {
  hiddenLayers: Store<ReadonlySet<number>>;
  hoveredLayer: Store<number | null>;
  /** Shared with `selectionController` — hiding a layer prunes this too. */
  selection: Store<Selection>;
  getModel: () => DrawingModel | null;
  viewport: Pick<ViewportController, 'frameBounds'>;
};

/**
 * Layer visibility, hover, and per-layer selection/framing.
 *
 * Also writes `selection` (shared with `selectionController`, not owned by
 * this file) — hiding a layer has to drop whatever of the selection it was
 * carrying, and that invariant belongs with the visibility change that causes
 * it, not scattered across every place that could hide a layer.
 */
export const createLayerController = ({
  hiddenLayers,
  hoveredLayer,
  selection,
  getModel,
  viewport,
}: Dependencies): LayerManager => {
  /**
   * Drops selected items a visibility change just hid.
   *
   * Pruning, not clearing: hiding one layer must leave selections on other
   * layers alone, and re-showing it does not bring back what was dropped. A
   * no-op prune leaves `selection` untouched — `pruneHiddenFromSelection`
   * returns the same reference when nothing is doomed, and the lens only
   * writes through to the parent store when the value actually changes.
   */
  const pruneSelection = (nextHiddenLayers: ReadonlySet<number>) => {
    const loops = getModel()?.loops ?? [];
    selection.setState((previous) =>
      pruneHiddenFromSelection(previous, loops, nextHiddenLayers),
    );
  };

  return {
    isVisible: (layerIndex) => !hiddenLayers.getState().has(layerIndex),

    setVisible(layerIndex, isVisible) {
      const previous = hiddenLayers.getState();
      if (previous.has(layerIndex) === !isVisible) {
        return;
      }
      const next = new Set(previous);
      if (isVisible) {
        next.delete(layerIndex);
      } else {
        next.add(layerIndex);
      }
      hiddenLayers.setState(() => next);
      pruneSelection(next);
    },

    setAllVisible(isVisible) {
      if (isVisible) {
        hiddenLayers.setState((previous) =>
          previous.size === 0 ? previous : new Set(),
        );
        return;
      }
      const next = new Set(
        (getModel()?.scene.layers ?? []).map((_layer, index) => index),
      );
      hiddenLayers.setState(() => next);
      pruneSelection(next);
    },

    setHovered(layerIndex) {
      hoveredLayer.setState((previous) =>
        previous === layerIndex ? previous : layerIndex,
      );
    },

    select(layerIndex, mode = 'replace') {
      const refs = loopsOnLayer(getModel()?.loops ?? [], layerIndex);
      selection.setState((previous) =>
        mode === 'add' ? addToSelection(previous, refs) : selectionOf(refs),
      );
    },

    frame(layerIndex) {
      const bounds = getModel()?.boundsByLayer.get(layerIndex);
      if (!bounds) {
        return;
      }
      viewport.frameBounds(bounds);
    },
  };
};
